import React, { useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import { AspectRatio, Grid, Skeleton } from '@mui/joy'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import { useUploadBackgroundImageMutation } from '../services/branding'

type FileUploadProps = {
  backgroundImageUrl: string | null
  setBackgroundImageUrl: (url: string) => void
  isUploadBackgroundImageLoading: boolean
  onUploadSuccess: () => void
  onUploadError: (error: unknown) => void
}

const useUploadBackgroundImage = () => {
  const [uploadBackgroundImage] = useUploadBackgroundImageMutation({
    fixedCacheKey: 'shared-delete-reward',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const uploadImage = async (formData: FormData) => {
    setIsLoading(true)
    setError(null)
    try {
      return await uploadBackgroundImage(formData).unwrap()
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return { uploadImage, isLoading, error }
}

function FileUpload(props: FileUploadProps) {
  const [id] = useState<string>(generateRandomString(10))

  const { uploadImage } = useUploadBackgroundImage()

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0] || null

    if (file) {
      const url = URL.createObjectURL(file)
      props.setBackgroundImageUrl(url)

      try {
        const formData = new FormData()
        formData.append('file', file)
        await uploadImage(formData)
        props.onUploadSuccess()
      } catch (err: unknown) {
        props.onUploadError(err)
      }
    } else {
      props.setBackgroundImageUrl('')
    }
  }

  function generateRandomString(length: number): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let result = ''

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charactersLength)
      result += characters[randomIndex]
    }

    return result
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {props.backgroundImageUrl ? (
        <Grid xs={12} md={12} container spacing={1}>
          <Box position="relative" width={120} height={120}>
            <AspectRatio ratio="1" sx={{ width: '100%', height: '100%' }}>
              <Box>
                <Skeleton
                  animation="wave"
                  loading={props.isUploadBackgroundImageLoading}
                >
                  <img
                    src={props.backgroundImageUrl}
                    alt="uploaded preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Skeleton>
              </Box>
            </AspectRatio>
            <Box
              position="absolute"
              top={5}
              right={5}
              bgcolor="background.body"
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow={1}
              sx={{ width: 20, height: 20, cursor: 'pointer' }}
              onClick={() => document.getElementById(id)?.click()}
            >
              <ChangeCircleIcon fontSize="small" />
            </Box>
          </Box>
        </Grid>
      ) : null}

      <input
        accept="image/*"
        style={{ display: 'none' }}
        id={id}
        type="file"
        onChange={handleFileChange}
      />

      {props.backgroundImageUrl === '' && (
        <Grid container justifyContent="center" alignItems="center">
          <label htmlFor={id}>
            <Grid sx={{ pt: 2 }}>
              <Button variant="soft" component="span">
                Kép feltöltése
              </Button>
            </Grid>
          </label>
        </Grid>
      )}
    </Box>
  )
}

export default FileUpload
